import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { applyPantonePalette, loadPantonePalette } from './components/PantonePalettePicker';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { WhitelabelProvider, useWhitelabel } from './contexts/WhitelabelContext';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger 
} from './components/ui/dropdown-menu';
import { ScrollArea } from './components/ui/scroll-area';
import { getInitials } from './lib/utils';
import { 
  House, Users, Buildings, Handshake, ClipboardText, Folder, 
  ShoppingCart, Gear, Sun, Moon, SignOut, CaretDown, List,
  FlowArrow, ChartBar, Bell, Robot, Briefcase, Storefront,
  CalendarBlank, TextT, ClockCounterClockwise, Megaphone, Globe, Shield, Database, UserCircle, LinkSimple, Truck, Key,
  Bank, IdentificationCard, UsersThree, TrendUp, Headset,
} from '@phosphor-icons/react';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordRequestsPage from './pages/PasswordRequestsPage';
import PricingPage from './pages/PricingPage';
import DemoRequestsPage from './pages/DemoRequestsPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import UseCasesPage from './pages/UseCasesPage';
import PartnersPage from './pages/PartnersPage';
import ResourcesPage from './pages/ResourcesPage';
import ResourceCategoryPage from './pages/ResourceCategoryPage';
import CMSAdminPage from './pages/CMSAdminPage';
import SupportPage from './pages/SupportPage';
import SupportAdminPage from './pages/SupportAdminPage';
import FinancePage from './pages/FinancePage';
import HRPage from './pages/HRPage';
import TeamsPage from './pages/TeamsPage';
import InvestPage from './pages/InvestPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import ContactsPage from './pages/ContactsPage';
import CompaniesPage from './pages/CompaniesPage';
import DealsPage from './pages/DealsPage';
import TasksPage from './pages/TasksPage';
import DocumentsPage from './pages/DocumentsPage';
import ProcurementPage from './pages/ProcurementPage';
import SettingsPage from './pages/SettingsPage';
import WorkflowBuilderPage from './pages/WorkflowBuilderPage';
import ReportsPage from './pages/ReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import AIAssistantPage from './pages/AIAssistantPage';
import BusinessIntelligencePage from './pages/BusinessIntelligencePage';
import BusinessMarketplacePage from './pages/BusinessMarketplacePage';
import ChatBubble from './components/ChatBubble';
import SubscriptionBanner from './components/SubscriptionBanner';
import CalendarPage from './pages/CalendarPage';
import CustomFieldsPage from './pages/CustomFieldsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import AffiliatePage from './pages/AffiliatePage';
import WebhooksPage from './pages/WebhooksPage';
import RBACPage from './pages/RBACPage';
import DealPrepLabPage from './pages/DealPrepLabPage';
import DataManagementPage from './pages/DataManagementPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientPortalView from './pages/ClientPortalView';
import SharedItemView from './pages/SharedItemView';
import ShareLinksDashboardPage from './pages/ShareLinksDashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import SupplyChainPage from './pages/SupplyChainPage';
import SchedulePage from './pages/SchedulePage';
import BookingSettingsPage from './pages/BookingSettingsPage';
import GroupTimeFinderPage from './pages/GroupTimeFinderPage';
import { PublicBookingProfilePage, PublicBookingEventPage } from './pages/PublicBookingPage';
import ManageBookingPage from './pages/ManageBookingPage';
import useScheduleReminders from './hooks/useScheduleReminders';

import './App.css';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', navKey: 'dashboard', icon: House },
  { path: '/schedule', label: 'Schedule & Remind', navKey: 'schedule', icon: CalendarBlank },
  { path: '/booking/settings', label: 'My booking page', navKey: 'bookingSettings', icon: LinkSimple },
  { path: '/booking/time-finder', label: 'Group time finder', navKey: 'timeFinder', icon: Globe },
  { path: '/contacts', label: 'Contacts', navKey: 'contacts', icon: Users },
  { path: '/companies', label: 'Companies', navKey: 'companies', icon: Buildings },
  { path: '/deals', label: 'Deals', navKey: 'deals', icon: Handshake },
  { path: '/tasks', label: 'Tasks', navKey: 'tasks', icon: ClipboardText },
  { path: '/documents', label: 'Documents', navKey: 'documents', icon: Folder },
  { path: '/procurement', label: 'Procurement', navKey: 'procurement', icon: ShoppingCart },
  { path: '/workflows', label: 'Workflows', navKey: 'workflows', icon: FlowArrow },
  { path: '/bi', label: 'Intelligence', navKey: 'intelligence', icon: Briefcase },
  { path: '/marketplace', label: 'Marketplace', navKey: 'marketplace', icon: Storefront },
  { path: '/deal-prep', label: 'Deal Prep Lab', navKey: 'dealPrep', icon: Briefcase },
  { path: '/projects', label: 'Projects', navKey: 'projects', icon: Briefcase },
  { path: '/supply-chain', label: 'Supply Chain', navKey: 'supplyChain', icon: Truck },
  { path: '/finance', label: 'Finance', navKey: 'finance', icon: Bank },
  { path: '/invest', label: 'Invest', navKey: 'invest', icon: TrendUp },
  { path: '/hr', label: 'Human Resources', navKey: 'hr', icon: IdentificationCard },
  { path: '/teams', label: 'Teams', navKey: 'teams', icon: UsersThree },
  { path: '/password-requests', label: 'Password Requests', navKey: 'passwordRequests', icon: Key, adminOnly: true },
  { path: '/demo-requests', label: 'Demo Requests', navKey: 'demoRequests', icon: Megaphone, adminOnly: true },
  { path: '/admin/content', label: 'Marketing CMS', navKey: 'cms', icon: TextT, adminOnly: true },
  { path: '/admin/support', label: 'Support Inbox', navKey: 'supportInbox', icon: Headset, adminOnly: true },
  { path: '/data-management', label: 'Data Management', navKey: 'dataManagement', icon: Database },
  { path: '/client-portal', label: 'Client Portal', navKey: 'clientPortal', icon: UserCircle },
  { path: '/share-links', label: 'Share Links', navKey: 'shareLinks', icon: LinkSimple },
  { path: '/affiliate', label: 'Affiliate', navKey: 'affiliate', icon: Megaphone },
  { path: '/reports', label: 'Reports', navKey: 'reports', icon: ChartBar },
  { path: '/calendar', label: 'Calendar', navKey: 'calendar', icon: CalendarBlank },
  { path: '/custom-fields', label: 'Custom Fields', navKey: 'customFields', icon: TextT },
  { path: '/audit-trail', label: 'Audit Trail', navKey: 'auditTrail', icon: ClockCounterClockwise },
  { path: '/webhooks', label: 'Webhooks', navKey: 'webhooks', icon: Globe },
  { path: '/rbac', label: 'Access Control', navKey: 'rbac', icon: Shield },
  { path: '/ai-assistant', label: 'AI Assistant', navKey: 'aiAssistant', icon: Robot },
  { path: '/notifications', label: 'Notifications', navKey: 'notifications', icon: Bell },
  { path: '/settings', label: 'Settings', navKey: 'settings', icon: Gear },
];

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function Sidebar({ mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { config: wl } = useWhitelabel();
  const [collapsed, setCollapsed] = React.useState(false);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        h-screen bg-card border-r border-border flex flex-col transition-all duration-200
        ${collapsed ? 'w-16' : 'w-64'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
      {/* Logo */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-2">
            {wl.logo_url && (
              <img src={wl.logo_url.startsWith('http') ? wl.logo_url : `${process.env.REACT_APP_BACKEND_URL}/api/documents/${wl.logo_url}/download`} alt="Logo" className="h-8 w-8 object-contain rounded-sm" />
            )}
            <div>
              <h1 className="font-heading text-lg font-bold tracking-tight">{wl.app_name || 'BIL'}</h1>
              <p className="text-xs text-muted-foreground">{wl.app_subtitle || 'Business Intelligence Lounge'}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className="h-8 w-8 p-0"
          data-testid="toggle-sidebar"
        >
          <List size={18} />
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="space-y-1 px-2">
          {NAV_ITEMS.filter((item) => !item.adminOnly || user?.role === 'admin').map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen && setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                {!collapsed && <span className="text-sm font-medium">{t(`nav.${item.navKey}`) !== `nav.${item.navKey}` ? t(`nav.${item.navKey}`) : item.label}</span>}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User Section */}
      <div className="p-2 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={`w-full flex items-center gap-3 p-2 rounded-sm hover:bg-muted transition-colors ${collapsed ? 'justify-center' : ''}`} data-testid="user-menu">
              <Avatar className="h-8 w-8 rounded-sm">
                {user?.picture ? (
                  <AvatarImage src={user.picture} alt={user.name} />
                ) : (
                  <AvatarFallback className="rounded-sm bg-primary/10 text-primary text-sm font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                )}
              </Avatar>
              {!collapsed && (
                <>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <CaretDown size={16} className="text-muted-foreground" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'light' && <Moon size={16} className="mr-2" />}
              {theme === 'dark' && <Shield size={16} className="mr-2" />}
              {theme === 'grc' && <Sun size={16} className="mr-2" />}
              {theme === 'light' && 'Dark Mode'}
              {theme === 'dark' && 'GRC Mode'}
              {theme === 'grc' && 'Light Mode'}
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Gear size={16} className="mr-2" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-menu">
              <SignOut size={16} className="mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
    </>
  );
}

function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <main className="flex-1 overflow-auto">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 p-4 border-b border-border bg-card sticky top-0 z-30">
          <Button variant="ghost" size="sm" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <List size={20} />
          </Button>
          <h1 className="font-heading text-lg font-bold">BIL</h1>
        </div>
        <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
          <SubscriptionBanner />
          {children}
        </div>
      </main>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // Check for OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/resources/:category" element={<ResourceCategoryPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/schedule" element={<SchedulePublicOrAppRoute />} />
      <Route
        path="/booking/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BookingSettingsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking/time-finder"
        element={
          <ProtectedRoute>
            <AppLayout>
              <GroupTimeFinderPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/book/:slug" element={<PublicBookingProfilePage />} />
      <Route path="/book/:slug/:eventSlug" element={<PublicBookingEventPage />} />
      <Route path="/r/:token" element={<ManageBookingPage />} />
      <Route path="/billing/success" element={<BillingSuccessPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ContactsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CompaniesPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DealsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TasksPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DocumentsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProcurementPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WorkflowBuilderPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ReportsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NotificationsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AIAssistantPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bi"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BusinessIntelligencePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BusinessMarketplacePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CalendarPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CustomFieldsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-trail"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AuditTrailPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AffiliatePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/webhooks"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WebhooksPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rbac"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RBACPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/password-requests"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PasswordRequestsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/demo-requests"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DemoRequestsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CMSAdminPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SupportAdminPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FinancePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HRPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TeamsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invest"
        element={
          <ProtectedRoute>
            <AppLayout>
              <InvestPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deal-prep"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DealPrepLabPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-management"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DataManagementPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-portal"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ClientPortalPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/share-links"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ShareLinksDashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProjectsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supply-chain"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SupplyChainPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/portal/:token" element={<ClientPortalView />} />
      <Route path="/share/:token" element={<SharedItemView />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function GlobalRemindersMount() {
  useScheduleReminders();
  return null;
}

/**
 * /schedule is a public free tool. Signed-in users see it inside the app
 * sidebar/AppLayout; guests get a lightweight public shell rendered by
 * SchedulePage itself (so guests don't need an account at all).
 */
function SchedulePublicOrAppRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  if (user) {
    return (
      <AppLayout>
        <SchedulePage />
      </AppLayout>
    );
  }
  return <SchedulePage />;
}

function App() {
  React.useEffect(() => {
    const saved = loadPantonePalette();
    if (saved) applyPantonePalette(saved);
  }, []);
  return (
    <ThemeProvider>
      <LanguageProvider>
        <WhitelabelProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
              <GlobalRemindersMount />
              <ChatBubble />
              <Toaster position="top-right" />
            </BrowserRouter>
          </AuthProvider>
        </WhitelabelProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
